<script setup lang="ts">
import { computed } from 'vue'
import { EStoreVariants } from './types'
import type { IButtonProps } from './types'
import { VButtonSizes, VButtonVariants } from '@/ui/plushComponents/VButton/types'
import { useDeviceOS } from '@/cms-ui/composables/useDeviceOs'
import useBreakpoint from '@/ui/composables/useBreakpoint'

const props = withDefaults(defineProps<IButtonProps>(), {
    variant: VButtonVariants.PRIMARY,
    size: VButtonSizes.SMALL,
})

const { isAndroid, isIOS } = useDeviceOS()
const { isDesktop } = useBreakpoint()

const store: Record<EStoreVariants, 'google' | 'apple'> = {
    [EStoreVariants.GOOGLE_PLAY]: 'google',
    [EStoreVariants.APP_STORE]: 'apple',
}

const showStoreButton = computed(() => {
    // On desktop, show both store buttons
    if (
        isDesktop.value &&
        (props.variant === EStoreVariants.GOOGLE_PLAY || props.variant === EStoreVariants.APP_STORE)
    ) {
        return true
    }

    // On mobile/tablet, show buttons based on device OS
    if (isAndroid.value || isIOS.value) {
        return (
            (props.variant === EStoreVariants.GOOGLE_PLAY && isAndroid.value) ||
            (props.variant === EStoreVariants.APP_STORE && isIOS.value)
        )
    } else {
        // If we don't know the OS, show both store buttons
        return props.variant === EStoreVariants.GOOGLE_PLAY || props.variant === EStoreVariants.APP_STORE
    }
})
</script>

<template>
    <VButtonApp
        v-if="showStoreButton"
        :data-test="dataTest"
        :link="href || ''"
        :store="variant ? store[variant as EStoreVariants] : store[EStoreVariants.APP_STORE]"
        class="!w-auto"
    />
    <VButton
        v-else-if="variant !== EStoreVariants.GOOGLE_PLAY && variant !== EStoreVariants.APP_STORE"
        :variant="variant"
        label="button"
        :size="size"
        :dark="dark"
        :state="state"
        :data-test="dataTest"
        :href="href"
        :to="to"
    >
        <slot />
    </VButton>
</template>
